import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GeolocationCoords } from '../../hooks/useGeolocation';
import { getCachedCoords, saveCoordsToCache } from '../../utils/geoCache';
import { useNearbyPartners } from '../../hooks/useNearbyPartners';
import { fetchAllServices } from '../../api/additionalServicesApi';
import { AdditionalServiceSummary } from '../../types/additionalServiceTypes';
import LocationGate from '../VitrinePage/components/LocationGate';
import PartnerList from './components/PartnerList';
import { AppHeader } from '../../components/shared/AppHeader';
import './ServicoDetalhePage.scss';

const RADIUS_OPTIONS = [5, 10, 20, 50] as const;

function ServicoDetalhePage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const parsedServiceId = serviceId ? parseInt(serviceId, 10) : null;
  const validServiceId = parsedServiceId && !isNaN(parsedServiceId) ? parsedServiceId : null;

  const [coords, setCoords] = useState<GeolocationCoords | null>(() => getCachedCoords());
  const [service, setService] = useState<AdditionalServiceSummary | null>(null);
  const [radius, setRadius] = useState<number>(() => {
    const parsed = Number(searchParams.get('radius'));
    return (RADIUS_OPTIONS as readonly number[]).includes(parsed) ? parsed : 10;
  });

  useEffect(() => {
    if (!validServiceId) return;
    fetchAllServices()
      .then((services) => {
        setService(services.find((s) => s.id === validServiceId) ?? null);
      })
      .catch(() => {});
  }, [validServiceId]);

  useEffect(() => {
    const title = service ? `${service.name} — WashPass` : 'Serviços Adicionais — WashPass';
    document.title = title;
    const metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (metaDesc) {
      metaDesc.content =
        service?.description ?? 'Parceiros WashPass oferecendo serviços adicionais na sua região.';
    }
    return () => {
      document.title = 'WashPass';
    };
  }, [service]);

  const { partners, loading, error } = useNearbyPartners(validServiceId, coords, radius);

  const handleLocationResolved = useCallback((resolvedCoords: GeolocationCoords) => {
    saveCoordsToCache(resolvedCoords);
    setCoords(resolvedCoords);
  }, []);

  function handleRadiusChange(event: React.ChangeEvent<HTMLSelectElement>) {
    setRadius(Number(event.target.value));
  }

  const serviceName = service?.name ?? 'Serviço Adicional';

  return (
    <>
      <AppHeader variant="dark" />
      <Box sx={{ pt: '72px', minHeight: '100vh' }}>
        {!coords ? (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Box sx={{ textAlign: 'center', px: 2, pt: 4, pb: 1 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                {serviceName}
              </Typography>
              {service?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
                  {service.description}
                </Typography>
              )}
            </Box>
            <LocationGate onLocationResolved={handleLocationResolved} />
          </Box>
        ) : (
          <div className="servico-detalhe">
            <div className="servico-detalhe__back">
              <button onClick={() => navigate('/vitrine')}>← Voltar aos serviços</button>
            </div>

            <div className="servico-detalhe__service-header">
              <div>
                <div className="servico-detalhe__service-name">{serviceName}</div>
                {service?.description && (
                  <div className="servico-detalhe__service-desc">{service.description}</div>
                )}
              </div>
              <select
                className="servico-detalhe__radius-select"
                value={radius}
                onChange={handleRadiusChange}
              >
                {RADIUS_OPTIONS.map((km) => (
                  <option key={km} value={km}>
                    Raio: {km} km
                  </option>
                ))}
              </select>
            </div>

            {!loading && (
              <div className="servico-detalhe__count">
                {partners.length} parceiro{partners.length !== 1 ? 's' : ''} encontrado
                {partners.length !== 1 ? 's' : ''} na sua região
              </div>
            )}

            <PartnerList
              partners={partners}
              loading={loading}
              error={error}
              serviceName={serviceName}
              serviceId={validServiceId}
            />
          </div>
        )}
      </Box>
    </>
  );
}

export default ServicoDetalhePage;
